import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { DEBUG } from 'app/auth/authRoles'
import { ReduxUser } from 'app/redux/ReduxInterfaces'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { joinWithDifferentLastSeparator } from 'utilsTS'
import { SalesforceFieldDescribe } from './components/formComponentsDefinitions'
import { getFormPicklistOptions } from './components/picklist/formPicklistUtils'
import { formConditionToText } from './pdf-components/formPdfUtils'
import { ConditionData } from './types/Conditions'
import { ConnectedMap, ElementsMap } from './types/FormTypes'

export const getObjectsIdsFromConnectedMap = (connectedMap: ConnectedMap, mainConnectedObject: string): {
  connectedAccount: string
  connectedSurvey: string
  connectedOpportunity: string
  editedObjectId: string
  connectedResource: string
} => {
  let connectedAccount, connectedOpportunity, connectedResource, connectedSurvey, editedObjectId

  Object.values(connectedMap).forEach((obj) => {
    if (obj.objectType === 'Opportunity') {
      connectedOpportunity = obj.sfObject.Id
    }
    if (obj.objectType === 'Account') {
      connectedAccount = obj.sfObject.Id
    }
    if (obj.objectType === 'Resource__c') {
      connectedResource = obj.sfObject.Id
    }
    if (obj.objectType === 'Survey__c') {
      connectedSurvey = obj.sfObject.Id
    }
    if (obj.objectType === mainConnectedObject) {
      editedObjectId = obj.sfObject.Id
    }
  })
  return { connectedAccount, connectedOpportunity, connectedResource, connectedSurvey, editedObjectId }
}

export function shouldBlockAutoSaveForUser (user: ReduxUser): boolean {
  const isTesting = sfOauthConfig.isTesting as boolean
  const isDebug = user.role.includes(DEBUG)
  return !isTesting && isDebug
}

/**
 * Checks complex Salesforce save results for error occurrences.
 * @param result The save result(s) to inspect.
 * @returns True if any error is found.
 */
export const checkIfSavingResultHasErrors = (result: any): boolean => {
  if (Array.isArray(result)) {
    return result.some((sub) => checkIfSavingResultHasErrors(sub))
  } else {
    if (result == null) {
      console.error(
        'No promise resolution configured or an unexpected error ocurred!'
      )
      return true
    }
    if (result.compositeResponse != null) {
      return result.compositeResponse.some((comp) => {
        if (comp.body == null) {
          return false
        } else {
          return comp.body.some((sub) => sub.success === false)
        }
      })
    }
    if ('isSuccess' in result) {
      if (result.outputValues != null && 'error' in result.outputValues) {
        return Boolean(result.outputValues.error)
      }
      return result.isSuccess === false
    }
    if ('success' in result) {
      return result.success === false
    }
    if ('hasErrors' in result) {
      return result.hasErrors
    }
    return false
  }
}

const ID_KEYS = ['id', 'Id']

const isPlainObject = (value: unknown): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype

const isMissingId = (value: unknown): boolean => value == null || value === ''

/**
 * Fills ids missing in nested objects of current value with ids present in fresh value at the same path.
 * Only id keys are added, other keys are left untouched. Arrays are skipped, as index based matching
 * could assign an id of a different record. Unchanged branches keep their references.
 * @param current Current value of the form element.
 * @param fresh Value of the form element calculated from freshly fetched data.
 * @param isRoot Whether current is the root value of the form element.
 * @returns Current value with filled ids.
 */
const fillMissingNestedIds = (current: unknown, fresh: unknown, isRoot: boolean): unknown => {
  if (!isPlainObject(current) || !isPlainObject(fresh)) {
    return current
  }
  let result = current
  const assign = (key: string, value: unknown): void => {
    if (result === current) {
      result = { ...current }
    }
    result[key] = value
  }
  // Root value of an element without id can be a deliberately selected new record (e.g. in pickers),
  // so ids are filled only for nested objects, which represent child records of the element
  if (!isRoot) {
    ID_KEYS.forEach((key) => {
      if (isMissingId(current[key]) && typeof fresh[key] === 'string' && fresh[key] !== '') {
        assign(key, fresh[key])
      }
    })
  }
  Object.keys(current).forEach((key) => {
    if (ID_KEYS.includes(key)) {
      return
    }
    const filled = fillMissingNestedIds(current[key], fresh[key], false)
    if (filled !== current[key]) {
      assign(key, filled)
    }
  })
  return result
}

/**
 * Fills ids of records created by a partially failed save into current form values, without overwriting any user input.
 * Prevents creating duplicated records when the form is saved again.
 * @param currentValues Current form values.
 * @param freshValues Initial values calculated from freshly fetched data.
 * @returns Current form values with filled ids.
 */
export const fillMissingIdsInFormValues = (
  currentValues: Record<string, any>,
  freshValues: Record<string, any>
): Record<string, any> => {
  let result = currentValues
  Object.keys(currentValues).forEach((key) => {
    const filled = fillMissingNestedIds(currentValues[key], freshValues[key], true)
    if (filled !== currentValues[key]) {
      if (result === currentValues) {
        result = { ...currentValues }
      }
      result[key] = filled
    }
  })
  return result
}

const PDF_LABELED_CONDITION_STATES = ['hide', 'show', 'required', 'notRequired']

/**
 * Builds texts describing when a form element should be answered, printed next to the element in PDF.
 * Conditions grouped in a parent condition are joined into one text, conditions of the group checking
 * the same element in the same way are merged into one phrase listing all compared values.
 * @param conditions Conditions of the form element.
 * @param type Type of the form element the conditions belong to.
 * @param langVersion Language the PDF is rendered in.
 * @param elementsMap Elements of the form, used to resolve condition targets.
 * @param idToRenderIndex Numbers of form elements as printed in the PDF.
 * @param connectedFieldDetails Describe of the Salesforce field connected to the element, used for dynamic picklist options.
 * @returns Texts of conditions relevant for filling the form.
 */
export const getPDFLabelFromConditions = ({
  elementsMap,
  type,
  connectedFieldDetails,
  idToRenderIndex,
  langVersion,
  conditions = []
}: {
  conditions: ConditionData[]
  type: string
  langVersion: 'en' | 'fr' | 'iu-cans'
  elementsMap: ElementsMap
  idToRenderIndex: Record<string, number>
  connectedFieldDetails: SalesforceFieldDescribe
}): string[] => {
  const isTargeted = (condition: ConditionData): condition is ConditionData & { conditionTarget: string } =>
    condition.conditionTarget != null && elementsMap[condition.conditionTarget] != null

  const parameterToText = (condition: ConditionData & { conditionTarget: string }, joinWord: string): string | undefined => {
    const target = elementsMap[condition.conditionTarget]
    const values = Array.isArray(condition.parameter) ? condition.parameter : [condition.parameter]
    let texts = values
    if (target.elementType === 'picklist') {
      const options = getFormPicklistOptions({
        connectedFieldDetails,
        typeProps: target.typeProps,
        langVersion,
        i18n
      })
      // Options are referenced either by API value or by their position in the editor, as `option<index>`
      texts = values.map((value) =>
        options.find((option, optionIndex) => option.apiValue === value || `option${optionIndex}` === value)?.title
      )
    }
    const definedTexts = texts.filter((text): text is string => text != null && text !== '')
    if (definedTexts.length === 0) {
      return undefined
    }
    return joinWithDifferentLastSeparator(definedTexts, ', ', ` ${joinWord} `)
  }

  const conditionToText = (
    condition: ConditionData & { conditionTarget: string },
    state: ConditionData['state'],
    joinWord: string
  ): string =>
    formConditionToText({
      condition: condition.condition,
      conditionTarger: condition.conditionTarget,
      parameter: parameterToText(condition, joinWord) ?? '',
      affectedElementType: type,
      elementType: elementsMap[condition.conditionTarget].elementType,
      targetIndex: String(idToRenderIndex[condition.conditionTarget]),
      state
    })

  // Only conditions about visibility or elements being required matter for filling the form
  const isLabeled = (condition: ConditionData): boolean =>
    PDF_LABELED_CONDITION_STATES.includes(condition.state) &&
    (Array.isArray(condition.conditions) ? condition.conditions.some(isTargeted) : isTargeted(condition))

  return conditions
    .filter(isLabeled)
    .map((condition) => {
      if (!Array.isArray(condition.conditions)) {
        return isTargeted(condition) ? conditionToText(condition, condition.state, i18n._(t`OR`)) : ''
      }

      const joinWord = condition.logic === 'any' ? i18n._(t`OR`) : i18n._(t`AND`)

      // Listing conditions checking the same element in the same way separately makes no sense, so they are
      // merged into one listing all their values. Copies are made, as conditions come from the form configuration
      const mergedConditions: Record<string, ConditionData & { conditionTarget: string, parameter: string[] }> = {}
      condition.conditions.filter(isTargeted).forEach((subCondition) => {
        const key = `${subCondition.conditionTarget}|${String(subCondition.condition)}`
        const values = (Array.isArray(subCondition.parameter) ? subCondition.parameter : [subCondition.parameter])
          .filter((value): value is string => value != null)
        const existing = mergedConditions[key]
        if (existing == null) {
          mergedConditions[key] = { ...subCondition, parameter: [...values] }
        } else {
          values.forEach((value) => {
            if (!existing.parameter.includes(value)) {
              existing.parameter.push(value)
            }
          })
        }
      })

      const subTexts = Object.values(mergedConditions).map((subCondition) =>
        conditionToText(subCondition, 'sub', joinWord)
      )

      let prefix = ''
      if (condition.state === 'show' || condition.state === 'required') {
        prefix = type === 'uploadFiles'
          ? String(i18n._(t`PDF_CONDITION_IF`))
          : String(i18n._(t`PDF_CONDITION_ANSWER_IF`))
      } else {
        prefix = String(i18n._(t`PDF_CONDITION_DO_NOT_ANSWER_IF`))
      }
      return `${prefix} ${subTexts.join(` ${joinWord} `)}`
    })
    .filter((text) => text !== '')
}
